import GW3Bridge from "./bridges/gw3Bridge";

import My from "./API/activityMyAPI";
import ActivityManager from "./core/activityManager";

import { ActivityBridge } from "./bridges/activityBridge";

import { ActivityConfig } from "./activityConfig";
import { ActivityAPI } from "./API/activityAPI";
import { ActivityAGM } from "./core/activityAGM";
import { Logger } from "./helpers/logger";
import * as util from "./helpers/util";

import { Glue42Core } from "@glue42/core";

import { nodeify } from "./helpers/promiseExtensions";

/**
 * Activity context update handler - used when we want to subscribe for context changes in a specific activity
 *
 * @callback contextChangedHandler
 * @param {Object} context The full context of the activity after the update
 * @param {Object} delta The delta (contains the added/removed keys at top level)
 * @param {string[]} removed  Array of keys that were removed from context
 * @param {module:activity} activity The activity instance that owns the context
 *
 */

/**
 * @callback windowEventHandler Handler for ActivityWindow events - e.g window joined to activity, window removed from
 * activity
 * @param {module:activity} activity
 * @param {module:activityWindow} window
 * @param {string} event 'joined' or 'removed'
 */

/**
 * @callback activityStatusChangeHandler Handler for activity status changed events
 * @param {module:activity} activity
 * @param {activityStatus} newStatus
 * @param {activityStatus} oldStatus
 *
 */

/**
 * @typedef {Object} windowDefinition
 * @property {string} type
 * @property {string} name
 * @property {Object} arguments
 */

/**
 * @typedef {Object} bounds
 * @param {number} top
 * @param {number} left
 * @param {number} width
 * @param {number} height
 *
 */

/**
 * @typedef {Object} agmInstance
 * @param {string} application
 * @param {string} pid
 * @param {string} machine
 * @param {string} user
 * @param {string} environment
 * @param {string} region
 */

// Factory function for activity API
export class ActivityModule {
    public static checkIsUsingGW3Implementation(connection: Glue42Core.Connection.API) {
        return connection.protocolVersion === 3;
    }

    private _api: ActivityAPI;
    private _isUsingGW3Implementation: boolean;
    private _isUsingHCImplementation: boolean;
    private _readyPromise: Promise<ActivityModule>;

    get api(): ActivityAPI {
        return this._api;
    }

    set api(value: ActivityAPI) {
        this._api = value;
    }

    constructor(config: ActivityConfig) {
        if (!config) {
            throw new Error("config can not be null");
        }

        // set log level
        if (!util.isUndefined(config.logLevel)) {
            Logger.Level = config.logLevel;
        }

        if (!util.isUndefinedOrNull(config.logger)) {
            Logger.GlueLogger = config.logger;
        }

        // choose the correct bridge
        let bridge: ActivityBridge;
        this._isUsingHCImplementation = config.gdMajorVersion === 2;
        this._isUsingGW3Implementation = ActivityModule.checkIsUsingGW3Implementation(config.connection);

        if (this._isUsingHCImplementation) {
            throw new Error("GD2 not supported");
        } else if (this._isUsingGW3Implementation) {
            bridge = new GW3Bridge(config);
        } else {
            throw new Error("Unable to instantiate activity bridge implementation");
        }

        if (!bridge) {
            throw new Error("A bridge to native activity is needed to create activity lib.");
        }

        ActivityAGM.AGM = config.agm;
        const activityManager = new ActivityManager(bridge, !config.disableAutoAnnounce, config.windows);
        const my = new My(activityManager, config.windows);
        this._api = new ActivityAPI(activityManager, my);
        this._readyPromise = activityManager.ready().then((_) => this);
    }

    public get isUsingHCImplementation(): boolean {
        return this._isUsingHCImplementation;
    }

    public get isUsingGW3Implementation(): boolean {
        return this._isUsingGW3Implementation;
    }

    public ready(callback?: (err: string, module: ActivityModule) => void): Promise<void> {
        return nodeify(this._readyPromise, callback) as any as Promise<void>;
    }
}
